import intersectionWith from "lodash.intersectionwith";

import { EmployerDocumentTag, EmployerNormalizedTag } from "../EmployeeServiceTypes";
import { dateToString } from "../../Helpers/DateHelpers";
import { EmployerDocumentsState } from "../EmployeeServiceState";
import { IterUtils } from "../../../../Common/IterableUtils";

import { tagFieldNameToArray } from "./Components/EmployerDocumentTagHelpers";

export const tagToObject = (tag: EmployerDocumentTag): { [key: string]: string } => {
    const [, , keysPart, valuesPart] = /([a-zA-Z0-9]+):([a-zA-Z0-9_]+):([\s\S]+)/g.exec(tag) ?? [];
    const keys = keysPart?.split("_") ?? [];
    const values =
        valuesPart
            ?.matchAll(/\$\$(.+)\$\$|(((?=[^_]).)+)/g)
            .map(it => it[0]?.replace(/\$\$/g, ""))
            .toArray() ?? [];
    const result: { [key: string]: string } = {};

    keys.forEach((key, index) => {
        result[key] = values[index] ?? "";
    });

    return result;
};

export const documentsToTagsListView = (
    employerDocuments?: Pick<EmployerDocumentsState, "documents" | "options">
): EmployerNormalizedTag[] => {
    const tagSchemaFieldNames = employerDocuments?.options?.columns.map(it => it.tagFieldName) ?? [];
    const tagsForView = employerDocuments?.options?.tagsForView ?? [];

    const employerDocumentTags =
        employerDocuments?.documents
            .map<EmployerNormalizedTag[]>(document => {
                let tags: string[] = [];

                if (employerDocuments?.options?.filterByEmployeeRelations && document.employeeRelations) {
                    tags = intersectionWith(
                        ...document.employeeRelations.map(it => it.tags),
                        (left: string, right: string) => left === right
                    );
                } else {
                    tags = document.tags ?? [];
                }

                return (
                    tags.map<EmployerNormalizedTag>((tag: EmployerDocumentTag) => ({
                        sourceTag: tag,
                        normalizedTag: {
                            ...tagToObject(tag),
                        },
                        documentId: document.id,
                        content: document.content as EmployerNormalizedTag["content"],
                        date: dateToString(new Date(document.date), "d MMMM в HH:mm"),
                        dateIso: document.date,
                    })) ?? []
                );
            })
            .flat()
            .filter(({ sourceTag }) => {
                const sourceTagLowerCase = sourceTag.toLowerCase();

                if (tagsForView.length > 0) {
                    const [_, tagSchema] = sourceTagLowerCase.split(":");
                    return tagsForView.some(tagForView => tagSchema === tagForView.toLowerCase());
                }

                return tagSchemaFieldNames.every(tagFieldName =>
                    tagFieldNameToArray(tagFieldName).every(singleTagFieldName =>
                        sourceTagLowerCase.includes(singleTagFieldName)
                    )
                );
            })
            .filter((tag: EmployerNormalizedTag) =>
                employerDocuments?.options?.filterTagsCallback != undefined
                    ? employerDocuments?.options?.filterTagsCallback(tag)
                    : true
            ) ?? [];

    return [
        ...IterUtils.distinctBy(employerDocumentTags, (tag: EmployerNormalizedTag) => {
            let id = tag.documentId;

            for (const tagSchemaFieldName of tagSchemaFieldNames) {
                for (const tagSchemaFieldNamePart of tagSchemaFieldName.split(",")) {
                    id += `_${tag.normalizedTag[tagSchemaFieldNamePart.toLowerCase()]}`;
                }
            }

            return id;
        }),
    ];
};
